import { Button, Textarea } from '@headlessui/react'
import {
  Truck,
  Tag,
  Box,
  Weight,
  Ruler,
  FileCheck,
  Search,
  Ship,
  Warehouse,
  Cat,
  CarFront,
  Package,
  Calendar,
  Hash
} from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { useDeliveryActions, useDeliveryState } from './stores/useDeliveryStore'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import DatePicker from '@/components/form/DatePicker'
import Select, { Option } from '@/components/form/Select'

const options: Option[] = [
  {
    label: '清关',
    value: 'CLEARANCE',
    icon: <Ship className="w-4 h-4 text-blue-600" />
  },
  {
    label: '仓库',
    value: 'WAREHOUSE',
    icon: <Warehouse className="w-4 h-4 text-orange-600" />
  },
  {
    label: '西农直发',
    value: 'DIRECT',
    icon: <Truck className="w-4 h-4 text-green-600" />
  },
  {
    label: '客户自提',
    value: 'CUSTOMER',
    icon: <CarFront className="w-4 h-4 text-purple-600" />
  },
  {
    label: '佐川物流',
    value: 'SAGAWA',
    icon: <Package className="w-4 h-4 text-red-600" />
  },
  {
    label: '黑猫物流',
    value: 'YAMATO',
    icon: <Cat className="w-4 h-4 text-gray-800" />
  }
]

export interface DeliveryWithSchedule {
  id: number
  scheduledOutboundDate: string
  deliveryOpt: string
  deliveryNote: string
}

const Delivery: React.FC = () => {
  const { houseBills, loading } = useDeliveryState()
  const { fetchDeliverys, refresh, confirmation, reject } = useDeliveryActions()

  // 用本地状态跟踪已确认的HBL
  const [confirmedHouseBills, setConfirmedHouseBills] = useState<Set<number>>(new Set())

  // 用本地草稿状态存储用户正在编辑但未提交的数据
  const [drafts, setDrafts] = useState<Map<number, Partial<import('@/services/CustomsV2Service').HouseBillSchema>>>(new Map())

  useEffect(() => {
    fetchDeliverys()
  }, [])

  // 仅在初始数据加载时设置已确认状态，避免用户操作时重复触发
  useEffect(() => {
    // 只有当 confirmedHouseBills 为空时才初始化（表示第一次加载）
    if (confirmedHouseBills.size === 0 && houseBills.length > 0) {
      const initialConfirmedIds = new Set<number>()
      houseBills.forEach((houseBill) => {
        // 如果HBL已经有预约搬出日期，说明之前已经确认过了
        if (houseBill.scheduledOutboundDate && houseBill.id) {
          initialConfirmedIds.add(houseBill.id)
        }
      })
      setConfirmedHouseBills(initialConfirmedIds)
    }
  }, [houseBills, confirmedHouseBills.size])

  // 更新 draft 数据
  const updateDraft = (houseBillId: number, field: string, value: string) => {
    setDrafts(prev => {
      const newDrafts = new Map(prev)
      const draft = newDrafts.get(houseBillId) || {}
      newDrafts.set(houseBillId, { ...draft, [field]: value })
      return newDrafts
    })
  }

  // 获取显示值（优先显示 draft，否则显示原始值）
  const getValue = (houseBillId: number, field: keyof import('@/services/CustomsV2Service').HouseBillSchema) => {
    const draft = drafts.get(houseBillId)
    const houseBill = houseBills.find(hb => hb.id === houseBillId)
    return (draft?.[field] ?? houseBill?.[field]) || ''
  }

  // 处理提交确认
  const handleConfirmation = async (houseBillId: number) => {
    const draft = drafts.get(houseBillId)
    const houseBill = houseBills.find(hb => hb.id === houseBillId)

    const scheduledOutboundDate = (draft?.scheduledOutboundDate ?? houseBill?.scheduledOutboundDate) as string
    const deliveryOpt = (draft?.deliveryOpt ?? houseBill?.deliveryOpt) as string
    const deliveryNote = ((draft?.deliveryNote ?? houseBill?.deliveryNote) || '') as string

    if (!scheduledOutboundDate || !deliveryOpt) {
      alert('请填写完整的预约搬出日期和派送选项')
      return
    }

    await confirmation(houseBillId, {
      scheduledOutboundDate,
      deliveryOpt,
      deliveryNote
    })

    // 确认成功后，清除 draft 并将此HBL添加到已确认列表
    setDrafts(prev => {
      const newDrafts = new Map(prev)
      newDrafts.delete(houseBillId)
      return newDrafts
    })
    setConfirmedHouseBills(prev => new Set(prev).add(houseBillId))
  }

  // 处理取消确认
  const handleReject = async (houseBillId: number) => {
    await reject(houseBillId)
    // 取消成功后，从已确认列表中移除
    setConfirmedHouseBills(prev => {
      const newSet = new Set(prev)
      newSet.delete(houseBillId)
      return newSet
    })
  }

  // 判断HBL是否已确认
  const isConfirmed = (houseBillId: number) => {
    return confirmedHouseBills.has(houseBillId)
  }

  const getStatusBadge = (status: string, statusDisplay: string) => {
    const baseClasses =
      'px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 flex items-center gap-1'

    switch (status) {
      case 'PENDING_OUTBOUND':
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            <Truck className="w-4 h-4" />
            {statusDisplay}
          </span>
        )
      case 'PENDING_INSPECTION':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <Search className="w-4 h-4" />
            {statusDisplay}
          </span>
        )
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            <FileCheck className="w-4 h-4" />
            {statusDisplay}
          </span>
        )
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-center pb-4 border-b border-gray-200">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">预约搬出管理</h1>
          <p className="mt-1 text-sm text-gray-500">
            共 {houseBills.length} 条待处理 HBL
          </p>
        </div>
        <button
          onClick={() => refresh()}
          disabled={loading}
          className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          刷新数据
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              显示状态为"可搬出"或"查验中"的 HBL，您可以为这些 HBL 设置预约搬出日期和派送方式
            </p>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {houseBills.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <p className="text-gray-500 text-sm">暂无可搬出或查验中的 HBL 数据</p>
        </div>
      ) : (
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
          {houseBills.map((houseBill) => (
            <div
              key={houseBill.id}
              className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Card Header */}
              <div className="flex justify-between items-start mb-5">
                <div className="flex items-center space-x-2">
                  <Hash className="w-4 h-4 text-gray-400" />
                  <span className="text-base font-semibold text-gray-900">
                    {houseBill.houseBlNumber}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {getStatusBadge(houseBill.status, houseBill.statusDisplay)}
                  {isConfirmed(houseBill.id) && houseBill.scheduledOutboundDate && (
                    <div className="px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {houseBill.scheduledOutboundDate}
                    </div>
                  )}
                </div>
              </div>

              {/* Statistics Grid */}
              <div className="grid grid-cols-4 gap-4 mb-5 pb-5 border-b border-gray-100">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-1">
                    <Tag className="w-4 h-4" />
                    唛头
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {houseBill.marksNumbers?.length || 0}
                  </div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-1">
                    <Box className="w-4 h-4" />
                    箱数
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {houseBill.packages || '-'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-1">
                    <Weight className="w-4 h-4" />
                    重量
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {houseBill.weightKg || '-'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-1">
                    <Ruler className="w-4 h-4" />
                    体积
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {houseBill.volumeCbm || '-'}
                  </div>
                </div>
              </div>

              {/* Form Section */}
              <div className="space-y-4">
                <DatePicker
                  label="预约搬出日期"
                  value={getValue(houseBill.id, 'scheduledOutboundDate') as string}
                  onChange={(value) => updateDraft(houseBill.id, 'scheduledOutboundDate', value)}
                  placeholder="请选择预约搬出日期"
                  minDate={new Date().toISOString().split('T')[0]}
                />

                <Select
                  placeholder="请选择派送方式"
                  value={getValue(houseBill.id, 'deliveryOpt') as string}
                  options={options}
                  onChange={(value) => updateDraft(houseBill.id, 'deliveryOpt', String(value))}
                />

                <Textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="配送信息（选填）"
                  rows={3}
                  value={getValue(houseBill.id, 'deliveryNote') as string}
                  onChange={(e) => updateDraft(houseBill.id, 'deliveryNote', e.target.value)}
                />

                {!isConfirmed(houseBill.id) ? (
                  <Button
                    className="w-full py-2.5 text-white text-sm font-medium bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    onClick={() => handleConfirmation(houseBill.id)}
                  >
                    确认搬出
                  </Button>
                ) : (
                  <Button
                    className="w-full py-2.5 text-white text-sm font-medium bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                    onClick={() => handleReject(houseBill.id)}
                  >
                    取消搬出
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Delivery
